import { useMemo, useState } from 'react';
import { Icon } from './Icon';
import { api, ApiError } from '../lib/api';

/**
 * Editor de factura con libertad total (pagos parciales, ajuste de cantidades,
 * precios y conceptos). Si el importe o la cantidad superan los del documento
 * origen, el backend avisa y aquí se muestra un aviso que hay que confirmar.
 * La razón del cambio es opcional y queda registrada en el historial.
 */

interface Line {
  description: string;
  quantity: number;
  unitPrice: string; // unidades mayores, tal como se teclea
  taxPct: number;
}

interface ExceedInfo {
  byAmount: boolean;
  byQuantity: boolean;
  origin: { number: string; totalMinor: number };
}

const toMinor = (s: string): number => Math.round((parseFloat(s.replace(',', '.')) || 0) * 100);
const fmt = (minor: number, cur: string): string =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: cur }).format(minor / 100);

export function InvoiceEditor({
  docId, docNumber, currency, initialLines, onClose, onSaved,
}: {
  docId: string;
  docNumber: string;
  currency: string;
  initialLines: Array<{ description: string; quantity: number; unitPriceMinor: number; taxPct: number }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [lines, setLines] = useState<Line[]>(
    initialLines.length
      ? initialLines.map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: (l.unitPriceMinor / 100).toString(), taxPct: l.taxPct }))
      : [{ description: '', quantity: 1, unitPrice: '', taxPct: 0 }],
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [exceed, setExceed] = useState<{ info: ExceedInfo; message: string } | null>(null);

  const setLine = (i: number, patch: Partial<Line>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { description: '', quantity: 1, unitPrice: '', taxPct: 0 }]);
  const delLine = (i: number) => setLines((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const total = useMemo(() => {
    let t = 0;
    for (const l of lines) {
      const base = l.quantity * toMinor(l.unitPrice);
      t += base + Math.round((base * l.taxPct) / 100);
    }
    return t;
  }, [lines]);

  const payload = (acknowledge: boolean) => ({
    lines: lines
      .filter((l) => l.description.trim())
      .map((l) => ({ description: l.description, quantity: l.quantity, unitPriceMinor: toMinor(l.unitPrice), taxPct: l.taxPct })),
    reason: reason.trim(),
    acknowledgeExceeds: acknowledge,
  });

  const save = async (acknowledge: boolean) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post<{ applied: boolean; exceeds?: ExceedInfo; message?: string }>(
        `/documents/${docId}/invoice-edit`, payload(acknowledge),
      );
      if (!r.applied && r.exceeds) {
        setExceed({ info: r.exceeds, message: r.message ?? 'Supera el documento origen.' });
        setBusy(false);
        return;
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo guardar la factura.');
      setBusy(false);
    }
  };

  return (
    <div className="pdfv-scrim" onMouseDown={onClose}>
      <div className="invedit" onMouseDown={(e) => e.stopPropagation()}>
        <div className="invedit__head">
          <div>
            <div className="eyebrow">Editar factura</div>
            <div className="invedit__num">{docNumber}</div>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>

        <div className="invedit__body">
          <div className="line-editor__head">
            <span>Concepto</span>
            <span className="r">Cant.</span>
            <span className="r">Precio</span>
            <span className="r">IVA %</span>
            <span />
          </div>
          {lines.map((l, i) => (
            <div className="line-editor__row line-editor__row--inv" key={i}>
              <input className="line-input" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Descripción del producto o servicio…" />
              <input className="line-input r" type="number" value={l.quantity} onChange={(e) => setLine(i, { quantity: Number(e.target.value) || 0 })} />
              <input className="line-input r" type="number" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} placeholder="0" />
              <input className="line-input r" type="number" value={l.taxPct} onChange={(e) => setLine(i, { taxPct: Number(e.target.value) || 0 })} />
              <button className="line-del" onClick={() => delLine(i)} title="Quitar línea"><Icon name="x" size={15} /></button>
            </div>
          ))}
          <button className="add-line" onClick={addLine}><Icon name="plus" size={15} /> Añadir línea</button>

          <div className="field" style={{ marginTop: 18 }}>
            <label>Razón del cambio <span className="faint">(opcional)</span></label>
            <textarea className="input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="Ej.: pago parcial acordado, ajuste de alcance, corrección de cantidades…" />
          </div>
        </div>

        <div className="invedit__foot">
          <div className="invedit__total">
            <span className="muted">Total factura</span>
            <span className="num">{fmt(total, currency)}</span>
          </div>
          {err && <div className="act-error" style={{ marginBottom: 10 }}><Icon name="alert" size={16} />{err}</div>}
          <button className="btn btn--primary" style={{ width: '100%' }} onClick={() => save(false)} disabled={busy}>
            {busy ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>

        {/* Pop-up de exceso */}
        {exceed && (
          <div className="invedit__alert-scrim" onMouseDown={() => setExceed(null)}>
            <div className="invedit__alert" onMouseDown={(e) => e.stopPropagation()}>
              <div className="invedit__alert-icon"><Icon name="alert" size={22} /></div>
              <h3>Supera el documento origen</h3>
              <p>{exceed.message}</p>
              <div className="invedit__alert-detail">
                <div><span className="muted">Origen ({exceed.info.origin.number})</span><span className="num">{fmt(exceed.info.origin.totalMinor, currency)}</span></div>
                <div><span className="muted">Esta factura</span><span className="num">{fmt(total, currency)}</span></div>
              </div>
              <p className="faint" style={{ fontSize: 12.5 }}>
                Puedes continuar igualmente. El cambio quedará registrado en el historial{reason.trim() ? ' con la razón indicada' : ''}.
              </p>
              <div className="invedit__alert-actions">
                <button className="btn btn--ghost" onClick={() => setExceed(null)}>Revisar</button>
                <button className="btn btn--primary" onClick={() => { setExceed(null); void save(true); }}>Continuar de todos modos</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
